import { useMemo, useState } from 'react'
import SkillsRadar from './SkillsRadar'
import PerformanceDetails from './PerformanceDetails'
import {
  getPedagogicalSnapshot,
  getStrongestAndWeakest,
  getCadenceHighlightsFromStats
} from '../utils/studentProgressInsights'
import './CompetencesSection.css'

function CompetencesSection({ attempts, profileStats, radarData }) {
  const [radarOpen, setRadarOpen] = useState(false)
  const pedagogical = useMemo(() => getPedagogicalSnapshot(attempts || [], 50), [attempts])
  const { strongestPoint } = useMemo(
    () => getStrongestAndWeakest(profileStats),
    [profileStats]
  )
  const cadenceHighlights = useMemo(
    () => getCadenceHighlightsFromStats(profileStats),
    [profileStats]
  )

  const hasEnoughData =
    profileStats &&
    ((Object.keys(profileStats.degreeStats || {}).length > 0 &&
      Object.values(profileStats.degreeStats || {}).some((s) => s.total >= 3)) ||
      (Object.keys(profileStats.cadenceStats || {}).length > 0 &&
        Object.values(profileStats.cadenceStats || {}).some((s) => s.total >= 3)))

  const hasPedago =
    pedagogical &&
    (pedagogical.byFunction.total > 0 ||
      pedagogical.byCadence.total > 0 ||
      pedagogical.byDegree.total > 0 ||
      pedagogical.byFigure.total > 0)

  if (!attempts?.length) {
    return (
      <div className="competences-section">
        <p className="competences-eyebrow">Compétences</p>
        <p className="competences-sparse">
          Pas encore assez de données. Complète quelques exercices pour voir ton bilan.
        </p>
      </div>
    )
  }

  return (
    <div className="competences-section">
      <p className="competences-eyebrow">Compétences</p>

      {!hasEnoughData ? (
        <p className="competences-sparse">
          Encore quelques réponses par notion (min. 3) pour un diagnostic fiable.
        </p>
      ) : strongestPoint ? (
        <p className="competences-strong-line">
          Point fort :{' '}
          {strongestPoint.isDegree ? (
            <span className="degree-roman">{strongestPoint.name}</span>
          ) : (
            strongestPoint.name
          )}{' '}
          · {strongestPoint.percentage} % de réussite
        </p>
      ) : null}

      {hasPedago ? (
        <div className="competences-pedago-grid">
          <div className="competences-pedago-card">
            <div className="competences-pedago-label">Fonctions</div>
            {pedagogical.byFunction.total > 0 ? (
              ['T', 'SD', 'D'].map((f) => {
                const data = pedagogical.byFunction[f]
                if (!data || data.total === 0) return null
                const label = f === 'T' ? 'Tonique' : f === 'SD' ? 'Sous-dom.' : 'Dominante'
                return (
                  <div key={f} className="competences-pedago-row">
                    <span className="competences-pedago-row-label">{label}</span>
                    <div className="competences-pedago-bar-wrap">
                      <div className="competences-pedago-bar" style={{ width: `${data.pct}%` }} />
                    </div>
                    <span className="competences-pedago-row-value">{data.pct}%</span>
                  </div>
                )
              })
            ) : (
              <p className="competences-pedago-empty">Pas encore de données</p>
            )}
          </div>
          <div className="competences-pedago-card">
            <div className="competences-pedago-label">Cadences</div>
            {cadenceHighlights.total > 0 ? (
              <>
                {cadenceHighlights.best ? (
                  <p className="competences-pedago-line competences-pedago-strong">
                    Meilleure : {cadenceHighlights.best.name} ({cadenceHighlights.best.pct}%)
                  </p>
                ) : null}
                {cadenceHighlights.weak &&
                cadenceHighlights.weak.name !== cadenceHighlights.best?.name ? (
                  <p className="competences-pedago-line competences-pedago-weak">
                    À travailler : {cadenceHighlights.weak.name} (
                    {cadenceHighlights.weak.pct}%)
                  </p>
                ) : null}
              </>
            ) : (
              <p className="competences-pedago-empty">Pas encore de données</p>
            )}
          </div>
          <div className="competences-pedago-card">
            <div className="competences-pedago-label">Degrés</div>
            {pedagogical.byDegree.total > 0 ? (
              <>
                {pedagogical.byDegree.strong ? (
                  <p className="competences-pedago-line competences-pedago-strong">
                    Point fort :{' '}
                    <span className="degree-roman">{pedagogical.byDegree.strong.degree}</span> (
                    {pedagogical.byDegree.strong.pct}%)
                  </p>
                ) : null}
                {pedagogical.byDegree.weak &&
                pedagogical.byDegree.weak.degree !== pedagogical.byDegree.strong?.degree ? (
                  <p className="competences-pedago-line competences-pedago-weak">
                    À travailler :{' '}
                    <span className="degree-roman">{pedagogical.byDegree.weak.degree}</span> (
                    {pedagogical.byDegree.weak.pct}%)
                  </p>
                ) : null}
              </>
            ) : (
              <p className="competences-pedago-empty">Pas encore de données</p>
            )}
          </div>
          <div className="competences-pedago-card">
            <div className="competences-pedago-label">Position</div>
            {pedagogical.byFigure.total > 0 ? (
              <>
                {pedagogical.byFigure.fundamental != null ? (
                  <div className="competences-pedago-row">
                    <span className="competences-pedago-row-label">Fondamentale</span>
                    <div className="competences-pedago-bar-wrap">
                      <div
                        className="competences-pedago-bar"
                        style={{ width: `${pedagogical.byFigure.fundamental.pct}%` }}
                      />
                    </div>
                    <span className="competences-pedago-row-value">
                      {pedagogical.byFigure.fundamental.pct}%
                    </span>
                  </div>
                ) : null}
                {pedagogical.byFigure.renversement != null ? (
                  <div className="competences-pedago-row">
                    <span className="competences-pedago-row-label">Renversements</span>
                    <div className="competences-pedago-bar-wrap">
                      <div
                        className="competences-pedago-bar"
                        style={{ width: `${pedagogical.byFigure.renversement.pct}%` }}
                      />
                    </div>
                    <span className="competences-pedago-row-value">
                      {pedagogical.byFigure.renversement.pct}%
                    </span>
                  </div>
                ) : null}
              </>
            ) : (
              <p className="competences-pedago-empty">Pas encore de données</p>
            )}
          </div>
        </div>
      ) : null}

      {radarData?.length > 0 ? (
        <details
          className="competences-details"
          open={radarOpen}
          onToggle={(e) => setRadarOpen(e.target.open)}
        >
          <summary>Vue d’ensemble (radar)</summary>
          <div className="competences-details-body">
            <SkillsRadar data={radarData} />
          </div>
        </details>
      ) : null}

      {profileStats &&
      (Object.keys(profileStats.degreeStats || {}).length > 0 ||
        Object.keys(profileStats.cadenceStats || {}).length > 0) ? (
        <details className="competences-details">
          <summary>Détail par degré et cadence</summary>
          <div className="competences-details-body">
            <PerformanceDetails
              degreeStats={profileStats.degreeStats}
              cadenceStats={profileStats.cadenceStats}
            />
          </div>
        </details>
      ) : null}
    </div>
  )
}

export default CompetencesSection
